import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bike, ShieldCheck, Mail, Loader2 } from "lucide-react";

export default function Login() {
  const { loginSuccess } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/auth/request-otp", { email });
      setStep(2);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      loginSuccess(data);
      navigate("/");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const inputCls = "bg-[#0B0F17] border-[#334155] text-slate-100 placeholder:text-slate-500 h-12 text-base";

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#3B82F6 1px, transparent 1px), linear-gradient(90deg, #3B82F6 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-lg bg-blue-600 flex items-center justify-center">
            <Bike className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-50" style={{ fontFamily: "Outfit, sans-serif" }}>OPM Bajaj</h1>
            <p className="text-xs uppercase tracking-widest text-slate-400">Dealership Dashboard</p>
          </div>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-6 sm:p-8 space-y-4">
          <Button data-testid="google-login-btn" onClick={googleLogin}
            className="w-full h-12 bg-white hover:bg-slate-100 text-slate-900 font-semibold text-base active:scale-95 transition-colors">
            <svg className="h-5 w-5 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C40.9 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
            Continue with Google
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-700" /><span className="text-xs text-slate-500">or get a code on email</span><div className="flex-1 h-px bg-slate-700" />
          </div>
          {step === 1 ? (
            <form onSubmit={requestOtp} className="space-y-3">
              <Input data-testid="email-input" required type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              {error && <p data-testid="auth-error" className="text-sm text-rose-400">{error}</p>}
              <Button data-testid="request-otp-btn" type="submit" disabled={busy}
                className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-base active:scale-95 transition-colors">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}<span className="ml-2">Email me a sign-in code</span>
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-3">
              <p className="text-sm text-slate-300">We sent a 6-digit code to <b>{email}</b></p>
              <Input data-testid="otp-input" required inputMode="numeric" maxLength={6} placeholder="● ● ● ● ● ●" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className={`${inputCls} text-center tracking-[0.5em] font-mono text-xl`} />
              {error && <p data-testid="auth-error" className="text-sm text-rose-400">{error}</p>}
              <Button data-testid="verify-otp-btn" type="submit" disabled={busy || code.length !== 6}
                className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-semibold text-base active:scale-95 transition-colors">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
              </Button>
              <button type="button" data-testid="otp-back-btn" onClick={() => { setStep(1); setCode(""); setError(""); }} className="w-full text-xs text-slate-400 hover:text-white">Use a different email</button>
            </form>
          )}
          <p className="text-xs text-slate-400 flex items-start gap-2 pt-1">
            <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            No passwords. Only emails whitelisted by your admin can sign in.
          </p>
        </div>
      </div>
    </div>
  );
}
